import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  makeStyles,
  CircularProgress
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import AppDialog from "components/Dialog/AppDialog";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { fetchGoalAnswers, getGoalAnswers, fetchClientGoals, getClientGoalsPage, updateMilestoneStatus, updateGoalStatus, deleteGoalTracker } from "store/slices/goalTracker";
import moment from "moment";
import AddGoalModal from "../form/AddGoalModal";
import ViewGoalDetailModal from "../form/ViewGoalDetailModal";
import { getLoadingGoals } from "store/slices/goalTracker";
import { getLoadingAnswers } from "store/slices/goalTracker";
import { GOAL_STATUS, MEASUREMENT_METHOD } from "config/AppConfig";
import UpdateGoalStatusModal from "../form/UpdateGoalStatusModal";
import ProgressNoteAnswersList from "../ProgressNoteAnswersList";

const useStyles = makeStyles(() => ({
  container: {
    paddingBottom: "10px",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: "#6B7280",
  },
  goalCard: {
    background: "#fff",
    border: "1px solid #dbdbdc",
    borderRadius: 8,
    padding: "15px",
    marginBottom: 15,
    boxShadow: "#00000026 1.95px 1.95px 2.6px",
  },
  goalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 10,
  },
  goalTitle: {
    fontSize: 16,
    paddingLeft: 2,
    marginRight: 8,
    fontWeight: 700,
    color: "#111827",
  },
  categoryPill: {
    padding: "3px 10px",
    borderRadius: 16,
    fontSize: 11,
    fontWeight: 600,
    border: "1px solid transparent",
  },
  statusPill: {
    padding: "3px 10px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    border: "1px solid transparent",
    width: 'fit-content'
  },
  categoryCoreSupport: {
    background: "#F0FDFA",
    color: "#0F766E",
  },
  categorySocialCommunity: {
    background: "#EFF6FF",
    color: "#1E40AF",
  },
  categoryCapacityBuilding: {
    background: "#F5F3FF",
    color: "#5B21B6",
  },
  progressBar: {
    width: "100%",
    height: 6,
    background: "#E5E7EB",
    borderRadius: 8,
    marginBottom: 10,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 8,
    transition: "width 0.3s ease",
  },
  progressCoreSupport: {
    background: "#2DD4BF",
  },
  progressSocialCommunity: {
    background: "#60A5FA",
  },
  progressCapacityBuilding: {
    background: "#A78BFA",
  },
  progressDetails: {
    display: "flex",
    gap: 15,
    alignItems: "center",
    marginBottom: 10,
  },
  progressPercent: {
    fontSize: 14,
    fontWeight: 700,
  },
  progressPercentCore: {
    color: "#0F766E",
  },
  progressPercentSocial: {
    color: "#2563EB",
  },
  progressPercentCapacity: {
    color: "#7C3AED",
  },
  progressText: {
    fontSize: 12,
    color: "#6B7280",
  },
  progressImprovement: {
    fontSize: 12,
    color: "#059669",
    display: "flex",
    alignItems: "center",
    gap: 3,
  },
  latestNote: {
    background: "#F8F9FA",
    borderRadius: 8,
    padding: "10px",
    marginBottom: 10,
    fontSize: 12,
    color: "#374151",
  },
  latestNoteTitle: {
    fontWeight: 700,
    color: "#111827",
  },
  milestoneContainer: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    marginBottom: 10,
  },
  milestonePill: {
    padding: "3px 10px",
    borderRadius: 16,
    fontSize: 11,
    fontWeight: 500,
    border: "1px solid transparent",
    cursor: "pointer",
    transition: "opacity 0.2s",
    "&:hover": {
      opacity: 0.8,
    }
  },
  milestoneCompleted: {
    background: "#ccfbf1",
    color: "#0f766e",
  },
  milestonePending: {
    background: "#F3F4F6",
    color: "#6B7280",
  },
  actionButtons: {
    display: "flex",
    gap: 8,
  },
  dialogContent: {
    padding: "15px 0",
  },
  noteItem: {
    padding: "12px 0",
    borderBottom: "1px solid #E5E7EB",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  noteHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  noteAuthor: {
    fontSize: 12,
    fontWeight: 700,
    color: "#111827",
  },
  noteDate: {
    fontSize: 12,
    color: "#6B7280",
  },
  qaList: {
    marginTop: 8,
  },
  qaItem: {
    marginBottom: 8,
  },
  question: {
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 3,
  },
  answer: {
    fontSize: 12,
    color: "#374151",
  },
}));

const GoalTrackerTab = ({ questions = [] }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id: clientId } = useParams();

  const [expandedGoals, setExpandedGoals] = useState({});
  const [goalPages, setGoalPages] = useState({});
  const [addGoalModalOpen, setAddGoalModalOpen] = useState(false);
  const [viewGoalModalOpen, setViewGoalModalOpen] = useState(false);
  const [selectedGoalForView, setSelectedGoalForView] = useState(null);
  const [addGoalInitialData, setAddGoalInitialData] = useState(null);
  const [confirmMilestone, setConfirmMilestone] = useState(null);
  const [updatingMilestone, setUpdatingMilestone] = useState(false);
  const [statusActionData, setStatusActionData] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const size = 5;

  const goalsPageData = useSelector(getClientGoalsPage);
  const goals = goalsPageData?.content || [];
  const goalsTotalPages = goalsPageData?.totalPages || 1;
  const [goalPage, setGoalPage] = useState(1);
  const goalAnswersMap = useSelector(getGoalAnswers);
  const loadingGoals = useSelector(getLoadingGoals);
  const loadingAnswers = useSelector(getLoadingAnswers);

  useEffect(() => {
    if (clientId) {
      dispatch(fetchClientGoals({ clientId, page: goalPage, size: 10 }));
    }
  }, [clientId, goalPage, dispatch]);

  const fetchGoals = (page = goalPage) => {
    dispatch(fetchClientGoals({ clientId, page, size: 10 }));
  };

  const getCategoryStyle = (category) => {
    switch (category) {
      case "CORE_SUPPORTS":
        return classes.categoryCoreSupport;
      case "CAPITAL_SUPPORTS":
        return classes.categorySocialCommunity;
      case "CAPACITY_BUILDING":
        return classes.categoryCapacityBuilding;
      default:
        return classes.categoryCoreSupport;
    }
  };

  const getProgressFillStyle = (category) => {
    switch (category) {
      case "CORE_SUPPORTS":
        return classes.progressCoreSupport;
      case "CAPITAL_SUPPORTS":
        return classes.progressSocialCommunity;
      case "CAPACITY_BUILDING":
        return classes.progressCapacityBuilding;
      default:
        return classes.progressCoreSupport;
    }
  };

  const getProgressPercentStyle = (category) => {
    switch (category) {
      case "CORE_SUPPORTS":
        return classes.progressPercentCore;
      case "CAPITAL_SUPPORTS":
        return classes.progressPercentSocial;
      case "CAPACITY_BUILDING":
        return classes.progressPercentCapacity;
      default:
        return classes.progressPercentCore;
    }
  };

  const formatCategory = (category) => {
    if (!category) return "";
    switch (category) {
      case "CORE_SUPPORT":
        return "Core support";
      case "SOCIAL_COMMUNITY":
        return "Social & community";
      case "CAPACITY_BUILDING":
        return "Capacity building";
      default:
        return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase().replace(/_/g, " ");
    }
  };

  const handleToggleNotes = (goalId) => {
    const isExpanding = !expandedGoals[goalId];
    setExpandedGoals((prev) => ({ ...prev, [goalId]: isExpanding }));
    
    if (isExpanding) {
      const currentPage = goalPages[goalId] || 1;
      setGoalPages(prev => ({ ...prev, [goalId]: currentPage }));
      dispatch(fetchGoalAnswers({ goalTrackerId: goalId, page: currentPage, size }));
    }
  };

  const handlePageChange = (goalId, value) => {
    setGoalPages(prev => ({ ...prev, [goalId]: value }));
    dispatch(fetchGoalAnswers({ goalTrackerId: goalId, page: value, size }));
  };

  const handleAddGoal = () => {
    setAddGoalInitialData(null);
    setAddGoalModalOpen(true);
  };

  const handleEditGoal = (goal) => {
    setAddGoalInitialData(goal);
    setAddGoalModalOpen(true);
  };

  const handleGoalPageChange = (event, value) => {
    setGoalPage(value);
  };

  const handleAddGoalSuccess = () => {
    dispatch(fetchClientGoals({ clientId, page: goalPage, size: 10 }));
  };

  const handleMilestoneClick = (milestone, goal) => {
    if (goal?.status !== GOAL_STATUS.ACTIVE) return;
    setConfirmMilestone(milestone);
  };

  const handleConfirmMilestoneStatus = async () => {
    if (!confirmMilestone) return;
    setUpdatingMilestone(true);
    const result = await dispatch(updateMilestoneStatus({ 
      milestoneId: confirmMilestone.id, 
      isCompleted: !confirmMilestone.isCompleted 
    }));
    setUpdatingMilestone(false);
    if (!result.error) {
      setConfirmMilestone(null);
      fetchGoals();
    }
  };

  const handleConfirmStatusAction = async (payload, goal) => {
    const result = await dispatch(updateGoalStatus({ id: goal.id, payload }));
    if (!result.error) {
      setStatusActionData(null);
      fetchGoals();
    }
  };

  const getWorkerName = (worker) => worker ? `${worker.firstName || ""} ${worker.lastName || ""}`.trim() : "Unknown";

  return (
    <Box className={classes.container}>
      <Box className={classes.sectionHeader}>
        <AppButton
          label={t("label.add_goal")}
          buttonColor={AppColor.mainColor}
          rounder
          onClick={handleAddGoal}
          startIcon={<i className="fas fa-plus" style={{ fontSize: 12 }}></i>}
        />
      </Box>

      {loadingGoals && (!goals || goals.length === 0) ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} style={{ color: AppColor.mainColor }} />
        </Box>
      ) : goals?.length > 0 ? (
        <Box position="relative">
          {loadingGoals && (
            <Box position="absolute" top={0} left={0} right={0} bottom={0} bgcolor="rgba(255,255,255,0.6)" zIndex={10} display="flex" justifyContent="center" alignItems="center" borderRadius={8}>
              <CircularProgress size={32} style={{ color: AppColor.mainColor }} />
            </Box>
          )}
          {goals.map((goal) => {
            const completedMilestones = goal.milestones?.filter(m => m.isCompleted).length || 0;
            const totalMilestones = goal.milestones?.length || 0;
            const isGoalActive = goal.status === GOAL_STATUS.ACTIVE;
          return (
            <Box key={goal.id} className={classes.goalCard}>
              <Box className={classes.goalHeader}>
                <Box display="flex" alignItems="center">
                  <Typography className={classes.goalTitle}>{goal.name}</Typography>
                  <Box 
                    className={classes.statusPill} 
                    style={{ 
                      backgroundColor: goal.status === GOAL_STATUS.ACTIVE ? "#DBEAFE" : goal.status === GOAL_STATUS.ACHIEVED ? "#D1FAE5" : goal.status === GOAL_STATUS.DISCONTINUED ? "#FEE2E2" : "#F3F4F6",
                      color: goal.status === GOAL_STATUS.ACTIVE ? "#1E40AF" : goal.status === GOAL_STATUS.ACHIEVED ? "#065F46" : goal.status === GOAL_STATUS.DISCONTINUED ? "#B91C1C" : "#374151",
                    }}
                  >
                    {t(`goalStatus.${goal.status}`)}
                  </Box>
                </Box>
                <Box className={`${classes.categoryPill} ${getCategoryStyle(goal.category)}`}>
                  {formatCategory(goal.category)}
                </Box>
              </Box>

              {goal.status === GOAL_STATUS.DISCONTINUED && goal.reasonDiscontinued && (
                <Box mb={2} p="10px" bgcolor="#FEF2F2" borderRadius={8} border="1px solid #FECACA">
                  <Box style={{ fontSize: 12, color: "#991B1B" }}>
                    <strong style={{ color: "#7F1D1D" }}>{t("label.reason", "Reason")}:</strong> {goal.reasonDiscontinued}
                  </Box>
                </Box>
              )}

              <Box className={classes.progressBar}>
                <Box
                  className={`${classes.progressFill} ${getProgressFillStyle(goal.category)}`}
                  style={{ width: `${goal.progressPercentage || 0}%` }}
                ></Box>
              </Box>

              {goal.measurementMethod === MEASUREMENT_METHOD.PERCENTAGE && (
                <Typography variant="body2" color="textSecondary">
                  {t("goal.progress", "Progress")}: {Number(parseFloat(goal.progressPercentage || 0).toFixed(2))}%
                </Typography>
              )}
              
              <Box className={classes.progressDetails}>
                <Typography className={`${classes.progressPercent} ${getProgressPercentStyle(goal.category)}`}>
                  {Number(parseFloat(goal.progressPercentage || 0).toFixed(2))}%
                </Typography>
                {
                  totalMilestones
                  ? <Typography className={classes.progressText}>
                    {completedMilestones} {t("label.of")} {totalMilestones} {t("label.milestones")}
                  </Typography>
                  : ''
                }
                <Typography className={classes.progressText}>
                  {t("label.review")}: {goal.targetDate ? moment(goal.targetDate).format("DD MMM YYYY") : "-"}
                </Typography>
              </Box>

              {goal.latestObservation && (
                <Box className={classes.latestNote}>
                  <span className={classes.latestNoteTitle}>
                    {t("label.latest")} ({goal.latestObservation.workerName}, {moment(goal.latestObservation.date).format("D MMM")}):
                  </span>
                  {" "}
                  {
                    goal.latestObservation.answer?.toUpperCase() === "YES" || goal.latestObservation.answer?.toUpperCase() === "NO"
                      ? t(`button.${goal.latestObservation.answer?.toLowerCase()}`)
                      : goal.latestObservation.answer
                  }
                </Box>
              )}

              {goal.milestones?.length > 0 && (
                <Box className={classes.milestoneContainer}>
                  {goal.milestones.map((milestone, idx) => (
                    <Box
                      key={idx}
                      className={`${classes.milestonePill} ${milestone.isCompleted ? classes.milestoneCompleted : classes.milestonePending}`}
                      style={{
                        cursor: isGoalActive ? "pointer" : "not-allowed",
                        opacity: isGoalActive ? 1 : 0.6,
                      }}
                      onClick={() => isGoalActive && handleMilestoneClick(milestone, goal)}
                    >
                      {milestone.isCompleted ? <i className="fas fa-check" style={{ marginRight: 4, fontSize: 10 }}></i> : <i className="far fa-circle" style={{ marginRight: 4, fontSize: 10 }}></i>}
                      {milestone.name}
                    </Box>
                  ))}
                </Box>
              )}

              <Box className={classes.actionButtons}>
                <AppButton
                  label={t("button.view_detail", "View Detail")}
                  buttonColor="transparent"
                  style={{
                    color: AppColor.mainColor,
                    border: `1px solid ${AppColor.mainColor}`,
                    background: "white",
                    fontSize: 11,
                    padding: "4px 10px",
                    height: 28,
                    minHeight: 28,
                  }}
                  onClick={() => {
                    setSelectedGoalForView(goal);
                    setViewGoalModalOpen(true);
                  }}
                  rounder
                />
                {
                  goal.status !== GOAL_STATUS.ACHIEVED && goal.status !== GOAL_STATUS.DISCONTINUED &&
                  <AppButton
                    label={t("button.edit")}
                    buttonColor="transparent"
                    style={{ color: "#F59E0B", border: "1px solid #F59E0B", background: "white", fontSize: 11, padding: "4px 10px", height: 28, minHeight: 28 }}
                    onClick={() => handleEditGoal(goal)}
                    rounder
                  />
                }

                {
                  goal.status !== GOAL_STATUS.ACHIEVED &&
                  <AppButton
                    label={t("button.change_status", "Change status")}
                    buttonColor="transparent"
                    style={{
                      color: AppColor.mainColor,
                      border: `1px solid ${AppColor.mainColor}`,
                      background: "white",
                      fontSize: 11,
                      padding: "4px 10px",
                      height: 28,
                      minHeight: 28,
                    }}
                    onClick={() => setStatusActionData({ type: 'STATUS', goal })}
                    rounder
                  />
                }

                {goal.status === GOAL_STATUS.DRAFT && (
                  <AppButton
                    label={t("button.delete", "Delete")}
                    buttonColor="transparent"
                    style={{ color: "#EF4444", border: "1px solid #EF4444", background: "white", fontSize: 11, padding: "4px 10px", height: 28, minHeight: 28 }}
                    onClick={() => setStatusActionData({ type: 'DELETE', goal })}
                    rounder
                  />
                )}

                <AppButton
                  label={expandedGoals[goal.id] ? t("label.hide_notes", "Hide notes") : t("label.view_all_notes")}
                  buttonColor="#F3F4F6"
                  style={{ color: "#374151", height: 28, minHeight: 28, border: "1px solid #e5e7eb" }}
                  rounder
                  onClick={() => handleToggleNotes(goal.id)}
                />
              </Box>

              {expandedGoals[goal.id] && (
                <Box mt={2}>
                  {(() => {
                    const currentGoalAnswers = goalAnswersMap[goal.id];
                    const answerList = currentGoalAnswers?.content || [];
                    const totalPages = currentGoalAnswers?.totalPages || 1;
                    const currentPage = goalPages[goal.id] || 1;
                    const isAnswersLoading = loadingAnswers;

                    const mappedAnswers = answerList.map(note => {
                      return {
                        ...note,
                        questionText: note?.questionText || "-",
                        workerName: getWorkerName(note.worker),
                        formattedDate: moment(note.createdDate).format("D MMM YYYY")
                      };
                    });

                    return (
                      <Box>
                        {isAnswersLoading && (!answerList || answerList.length === 0) ? (
                          <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={24} style={{ color: AppColor.mainColor }} />
                          </Box>
                        ) : answerList?.length > 0 ? (
                          <Box position="relative">
                            {isAnswersLoading && (
                              <Box position="absolute" top={0} left={0} right={0} bottom={0} bgcolor="rgba(255,255,255,0.6)" zIndex={10} display="flex" justifyContent="center" alignItems="center" borderRadius={8}>
                                <CircularProgress size={32} style={{ color: AppColor.mainColor }} />
                              </Box>
                            )}
                            <ProgressNoteAnswersList
                              answers={mappedAnswers}
                              loading={false}
                              t={t}
                            />
                            {totalPages > 1 && (
                              <Box display="flex" justifyContent="center" mt={2}>
                                <Pagination 
                                  count={totalPages} 
                                  page={currentPage} 
                                  onChange={(e, value) => handlePageChange(goal.id, value)} 
                                />
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Typography style={{ display: "flex", justifyContent: "center", width: "100%", padding: "20px 0", color: "#9CA3AF" }}>
                            {t("label.no_data")}
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}
                </Box>
              )}
            </Box>
          );
          })}
        </Box>
      ) : (
        <Box py={4} textAlign="center" color="#9CA3AF">
          {t("label.no_goals_found")}
        </Box>
      )}

      {goalsTotalPages > 0 && (
        <Box display="flex" justifyContent="center" mt={2} mb={4}>
          <Pagination count={goalsTotalPages} page={goalPage} onChange={handleGoalPageChange} />
        </Box>
      )}

      <AppDialog
        open={Boolean(confirmMilestone)}
        onClose={() => setConfirmMilestone(null)}
        title={t("label.confirm_milestone_update", "Update Milestone")}
      >
        <Box p={2}>
          <Typography>
            <Trans
              i18nKey={confirmMilestone?.isCompleted ? "goal.messageMilestoneNotCompleted" : "goal.messageMilestoneCompleted"}
              components={{ 1: <strong /> }}
            />
          </Typography>
          <Box display="flex" justifyContent="flex-end" mt={3} style={{ gap: 8 }}>
            <AppButton
              label={t("button.cancel")}
              onClick={() => setConfirmMilestone(null)}
              buttonColor="#F3F4F6"
              style={{ color: "#111827" }}
              rounder
            />
            <AppButton
              label={t("button.confirm")}
              onClick={handleConfirmMilestoneStatus}
              isLoading={updatingMilestone}
              disabled={updatingMilestone}
              buttonColor={AppColor.mainColor}
              rounder
            />
          </Box>
        </Box>
      </AppDialog>



      {statusActionData?.type === 'STATUS' && (
        <AppDialog
          open={true}
          onClose={() => setStatusActionData(null)}
          title={t("label.updateStatus")}
          maxWidth="sm"
        >
          <UpdateGoalStatusModal
            open={true}
            onClose={() => setStatusActionData(null)}
            onConfirm={(payload) => handleConfirmStatusAction(payload, statusActionData.goal)}
            goal={statusActionData.goal}
            isLoading={updatingStatus}
            questions={questions}
          />
        </AppDialog>
      )}

      <AppDialog
        open={statusActionData?.type === 'DELETE'}
        onClose={() => setStatusActionData(null)}
        title={t("label.delete_goal", "Delete Goal")}
      >
        <Box p={2}>
          <Typography>{t("label.confirm_delete_goal", "Are you sure you want to delete this goal?")}</Typography>
          <Box display="flex" justifyContent="flex-end" mt={3} style={{ gap: 8 }}>
            <AppButton label={t("button.cancel")} onClick={() => setStatusActionData(null)} buttonColor="#F3F4F6" style={{ color: "#111827" }} rounder />
            <AppButton label={t("button.confirm")} onClick={async () => {
              setUpdatingStatus(true);
              const result = await dispatch(deleteGoalTracker(statusActionData.goal.id));
              setUpdatingStatus(false);
              if (!result.error) {
                setStatusActionData(null);
                fetchGoals();
              }
            }} isLoading={updatingStatus} disabled={updatingStatus} buttonColor={AppColor.mainColor} rounder />
          </Box>
        </Box>
      </AppDialog>

      <AddGoalModal
        open={addGoalModalOpen}
        onClose={() => setAddGoalModalOpen(false)}
        clientId={clientId}
        initialData={addGoalInitialData}
        questions={questions}
        onSuccess={handleAddGoalSuccess}
      />

      <ViewGoalDetailModal
        open={viewGoalModalOpen}
        onClose={() => {
          setViewGoalModalOpen(false);
          setSelectedGoalForView(null);
        }}
        goal={selectedGoalForView}
        questions={questions}
      />
    </Box>
  );
};

export default GoalTrackerTab;
